<template>
  <div
    class="lang-switch"
    :class="{ 'is-dragging': dragging }"
    :style="{ top: top + 'px' }"
    role="button"
    tabindex="0"
    :aria-label="ariaLabel"
    :title="ariaLabel"
    @touchstart="onTouchStart"
    @mousedown="onMouseDown"
    @click="onClick"
    @keydown.enter.space.prevent="onToggle"
  >
    {{ label }}
  </div>
</template>
<script>
import { toggleLocale } from "../i18n";

const POS_STORAGE_KEY = "app_lang_switch_top"; // 拖动后的位置记忆
const BTN_SIZE = 40; // 与 .lang-switch 的宽高保持一致
const EDGE_GAP = 8; // 距离屏幕上/下边缘的最小间距
const DRAG_THRESHOLD = 6; // 超过这个位移才算拖动，否则视为点按
const DEFAULT_TOP = 12; // 默认停靠：右上角

export default {
  name: "lang_switch",
  data() {
    return {
      top: DEFAULT_TOP,
      dragging: false,
      moved: false,
      startY: 0,
      startTop: 0,
      useTouch: false,
    };
  },
  computed: {
    // 按钮显示"点击后会切到的目标语言"：英文页面显示 中，中文页面显示 EN
    // 这样和下面的 ariaLabel 表达一致
    label() {
      return this.$i18n.locale === "zh" ? "EN" : "中";
    },
    ariaLabel() {
      return this.$i18n.locale === "zh" ? "Switch to English" : "切换为中文";
    },
  },
  created() {
    this.top = this.clampTop(this.readSavedTop());
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
    this.top = this.clampTop(this.top);
  },
  beforeDestroy() {
    this.stopDrag();
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    // ---------- 位置 ----------
    maxTop() {
      // 用 window.innerHeight 而不是固定值，旋转屏幕/键盘弹出后依然贴边
      return Math.max(EDGE_GAP, window.innerHeight - BTN_SIZE - EDGE_GAP);
    },
    clampTop(value) {
      const num = Number(value);
      if (isNaN(num)) return DEFAULT_TOP;
      return Math.min(Math.max(num, EDGE_GAP), this.maxTop());
    },
    readSavedTop() {
      try {
        const saved = window.localStorage.getItem(POS_STORAGE_KEY);
        return saved === null ? DEFAULT_TOP : saved;
      } catch (e) {
        return DEFAULT_TOP;
      }
    },
    saveTop() {
      try {
        window.localStorage.setItem(
          POS_STORAGE_KEY,
          String(Math.round(this.top))
        );
      } catch (e) {
        // 写入失败（隐私模式等）不影响功能，忽略
      }
    },
    onResize() {
      this.top = this.clampTop(this.top);
    },

    // ---------- 拖动（仅上下） ----------
    startDrag(clientY, useTouch) {
      this.dragging = true;
      this.moved = false;
      this.useTouch = useTouch;
      this.startY = clientY;
      this.startTop = this.top;

      const moveEvent = useTouch ? "touchmove" : "mousemove";
      const endEvent = useTouch ? "touchend" : "mouseup";
      document.addEventListener(
        moveEvent,
        this.onDragMove,
        useTouch ? { passive: false } : false
      );
      document.addEventListener(endEvent, this.stopDrag);
      document.addEventListener("touchcancel", this.stopDrag);
      // 鼠标移出窗口后松手不会再触发 mouseup，兜底结束拖动，避免按钮"黏"着光标
      document.addEventListener("mouseleave", this.stopDrag);
    },
    onDragMove(e) {
      const point = this.useTouch ? e.touches[0] : e;
      if (!point) return;

      const delta = point.clientY - this.startY;
      if (!this.moved && Math.abs(delta) > DRAG_THRESHOLD) {
        this.moved = true;
      }
      if (!this.moved) return;

      // 拖动时阻止页面跟着滚动
      if (this.useTouch && e.cancelable) e.preventDefault();
      this.top = this.clampTop(this.startTop + delta);
    },
    stopDrag() {
      if (!this.dragging) return;
      this.dragging = false;
      document.removeEventListener("touchmove", this.onDragMove);
      document.removeEventListener("mousemove", this.onDragMove);
      document.removeEventListener("touchend", this.stopDrag);
      document.removeEventListener("mouseup", this.stopDrag);
      document.removeEventListener("touchcancel", this.stopDrag);
      document.removeEventListener("mouseleave", this.stopDrag);
      if (this.moved) this.saveTop();
    },
    onTouchStart(e) {
      if (e.touches.length !== 1) return;
      this.startDrag(e.touches[0].clientY, true);
    },
    onMouseDown(e) {
      if (e.button !== 0) return;
      e.preventDefault(); // 避免拖动时选中页面文字
      this.startDrag(e.clientY, false);
    },

    // ---------- 点按切换 ----------
    // 拖动结束后浏览器还会补发 click，用 moved 标记把它和真正的点按区分开
    onClick() {
      if (this.moved) {
        this.moved = false;
        return;
      }
      this.onToggle();
    },
    onToggle() {
      toggleLocale();
    },
  },
};
</script>
<style scoped>
.lang-switch {
  position: fixed;
  right: 12px;
  width: 40px;
  height: 40px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #dcdee0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.14);
  color: #1989fa;
  font-size: 15px;
  font-weight: 400;
  line-height: 1;
  /* 高于页面内容（最高 z-index:100），低于 Vant 弹层（2000+） */
  z-index: 1000;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: none; /* 从按钮上开始滑动时不滚动页面 */
  transition: box-shadow 0.2s, transform 0.2s;
}

.lang-switch.is-dragging {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.24);
  transform: scale(1.06);
}

.lang-switch:active {
  background: #f2f8ff;
}
</style>
